import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileSignature, Download, Plus, Eye, Trash2, Scale, ChevronRight,
  CheckCircle2, AlertCircle, Search,
} from "lucide-react";
import { formatDate } from "../lib/date";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";

const CONTRACT_TYPE_LABELS = {
  CTI: "Indefinido", CTD: "Plazo determinado", OD: "Obra determinada",
};

function fmt(iso) {
  return formatDate(iso);
}

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openGen, setOpenGen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [place, setPlace] = useState("Quepos");
  const [notes, setNotes] = useState("");
  const [genError, setGenError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [empSearch, setEmpSearch] = useState("");

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        api.get("/contracts"),
        api.get("/contracts/employees-without-contract"),
      ]);
      setContracts(c.data || []);
      setEligible(e.data || []);
    } catch {
      toast.error("No se pudieron cargar los contratos");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = contracts.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.employee_name || "").toLowerCase().includes(q) ||
      (c.contract_number || "").toLowerCase().includes(q) ||
      (c.employee_code || "").toLowerCase().includes(q)
    );
  });

  const filteredEligible = eligible.filter((e) => {
    if (!empSearch) return true;
    const q = empSearch.toLowerCase();
    return (
      (e.full_name || "").toLowerCase().includes(q) ||
      (e.employee_code || "").toLowerCase().includes(q) ||
      (e.national_id || "").toLowerCase().includes(q)
    );
  });

  const handleGenerate = async () => {
    if (!selectedEmp) { toast.error("Seleccione un trabajador"); return; }
    setGenerating(true);
    setGenError("");
    try {
      const { data } = await api.post("/contracts/generate", {
        employee_id: selectedEmp,
        generation_place: place,
        notes,
      });
      toast.success(`Contrato ${data.contract_number} generado`);
      setOpenGen(false);
      setSelectedEmp(""); setNotes(""); setEmpSearch("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error al generar contrato";
      if (e?.response?.status === 422) {
        setGenError(detail); // informe persistente dentro del diálogo
      } else {
        toast.error(detail);
      }
    } finally { setGenerating(false); }
  };

  const handleVoid = async (c) => {
    if (!window.confirm(`¿Anular el contrato ${c.contract_number}? El empleado podrá tener un nuevo contrato generado.`)) return;
    try {
      await api.post(`/contracts/${c.id}/void`);
      toast.success("Contrato anulado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al anular");
    }
  };

  const handleDownload = (c) => {
    window.open(`${apiBase}/api/contracts/${c.id}/download`, "_blank");
  };

  return (
    <div data-testid="contracts-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Scale className="w-5 h-5 text-zinc-700" strokeWidth={1.7} />
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
              Historial Legal · Contratos
            </h1>
          </div>
          <p className="text-sm text-zinc-500">
            Generación automática de contratos individuales de trabajo bajo el Código de Trabajo de Costa Rica
          </p>
        </div>
        <Button
          onClick={() => setOpenGen(true)}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="contract-generate-trigger"
        >
          <Plus className="w-4 h-4 mr-2" /> Generar contrato
        </Button>
      </div>

      <div className="mb-5 flex items-center gap-3 max-w-md">
        <Search className="w-4 h-4 text-zinc-400" />
        <Input
          placeholder="Buscar por trabajador o número de contrato…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9"
          data-testid="contracts-search"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-400">Cargando contratos…</div>
      ) : filtered.length === 0 ? (
        <Card className="py-16 px-6 text-center border-dashed">
          <FileSignature className="w-10 h-10 text-zinc-300 mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-base font-medium text-zinc-700">Sin contratos generados aún</p>
          <p className="text-sm text-zinc-500 mt-1">Hay {eligible.length} trabajadores sin contrato. Genere el primero con el botón de arriba.</p>
        </Card>
      ) : (
        <Card className="divide-y divide-zinc-100 overflow-hidden">
          {filtered.map((c) => (
            <div
              key={c.id}
              className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center group"
              data-testid={`contract-row-${c.id}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                c.voided_at ? "bg-zinc-100" : "bg-emerald-100"
              }`}>
                {c.voided_at ? (
                  <AlertCircle className="w-5 h-5 text-zinc-400" strokeWidth={1.6} />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-emerald-700" strokeWidth={1.6} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-zinc-500">{c.contract_number}</span>
                  <span className="font-semibold text-zinc-900 truncate">{c.employee_name}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {CONTRACT_TYPE_LABELS[c.contract_type] || c.contract_type}
                  </Badge>
                  {c.voided_at && <Badge variant="outline" className="text-[10px] text-zinc-400 border-zinc-300">Anulado</Badge>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                  <span>Inicio: <span className="text-zinc-700 font-medium">{fmt(c.effective_date)}</span></span>
                  {c.end_date && <span>Fin: <span className="text-zinc-700 font-medium">{fmt(c.end_date)}</span></span>}
                  <span>Generado: <span className="text-zinc-700 font-medium">{fmt(c.created_at)}</span></span>
                  <span className="text-zinc-400">{(c.clauses_snapshot || []).length} cláusulas</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(c)}
                  data-testid={`contract-download-${c.id}`}
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                </Button>
                {c.employee_exists !== false ? (
                  <Link
                    to={`/empleados/${c.employee_id}`}
                    className="inline-flex items-center gap-1 px-3 h-8 rounded-md text-xs text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                    data-testid={`contract-expediente-${c.id}`}
                  >
                    Expediente <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 px-3 h-8 rounded-md text-xs text-zinc-300 cursor-not-allowed"
                    title="El expediente de este empleado ya no está disponible"
                    data-testid={`contract-expediente-disabled-${c.id}`}
                  >
                    Expediente <AlertCircle className="w-3.5 h-3.5" />
                  </span>
                )}
                {!c.voided_at && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleVoid(c)}
                    className="h-8 w-8 text-zinc-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`contract-void-${c.id}`}
                    title="Anular contrato"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Generate dialog */}
      <Dialog open={openGen} onOpenChange={setOpenGen}>
        <DialogContent className="sm:max-w-lg" data-testid="contract-generate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-zinc-700" />
              Generar Contrato Individual de Trabajo
            </DialogTitle>
            <DialogDescription>
              Se generará un PDF estilo bufete con 21 cláusulas fundamentadas en el Código de Trabajo de Costa Rica.
              Los datos se toman del expediente del trabajador.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {genError && (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex gap-2.5"
                data-testid="contract-gen-error"
              >
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-[12px] leading-relaxed text-amber-900">{genError}</p>
                  {selectedEmp && (
                    <Link
                      to={`/empleados/${selectedEmp}`}
                      className="inline-flex items-center gap-1 text-[12px] font-medium text-amber-800 underline hover:text-amber-900"
                      data-testid="contract-gen-error-link"
                    >
                      Ir al expediente del trabajador <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Trabajador ({filteredEligible.length} sin contrato)
              </Label>
              <Input
                placeholder="Buscar por nombre, código o cédula…"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
                className="mt-1.5 h-8 text-sm"
                data-testid="contract-emp-search"
              />
              <div className="mt-2 max-h-60 overflow-y-auto border border-zinc-200 rounded-md divide-y divide-zinc-100">
                {filteredEligible.slice(0, 50).map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => { setSelectedEmp(e.id); setGenError(""); }}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-zinc-50 ${
                      selectedEmp === e.id ? "bg-zinc-100 ring-1 ring-zinc-900" : ""
                    }`}
                    data-testid={`contract-emp-${e.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 truncate">{e.full_name}</p>
                      <p className="text-[11px] text-zinc-500">
                        {e.national_id} · ingreso {fmt(e.hire_date)}
                      </p>
                    </div>
                    {selectedEmp === e.id && <CheckCircle2 className="w-4 h-4 text-emerald-700" />}
                  </button>
                ))}
                {filteredEligible.length === 0 && (
                  <p className="text-center text-xs text-zinc-400 py-6">Sin trabajadores sin contrato</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Lugar de suscripción</Label>
                <Input
                  value={place}
                  onChange={(e) => setPlace(e.target.value)}
                  placeholder="Quepos"
                  data-testid="contract-place"
                />
              </div>
              <div>
                <Label className="text-xs">Notas internas (opcional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ej: contrato 2026 · sustituye al original"
                  data-testid="contract-notes"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenGen(false)} disabled={generating}>
              Cancelar
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || !selectedEmp}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="contract-generate-submit"
            >
              {generating ? "Generando…" : "Generar contrato"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
